import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import StatsCard from '../components/StatsCard';
import ReportDetailModal from '../components/ReportDetailModal';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentTextIcon,
  EyeIcon,
  CheckIcon,
  ChartBarIcon,
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  CalendarIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [filters, setFilters] = useState({
    district: '',
    constituency: '',
    timeline: '',
    status: '',
  });

  // Utility functions for professional number formatting
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
  };

  const formatNumberWithCommas = (num: number): string => {
    return num.toLocaleString();
  };

  // If coordinator, enforce district scoping
  const coordinatorDistrict = user?.role === 'coordinator' ? (user.district || '') : '';

  useEffect(() => {
    if (coordinatorDistrict) {
      setFilters((prev) => ({ ...prev, district: coordinatorDistrict }));
    }
  }, [coordinatorDistrict]);

  const effectiveFilters = useMemo(() => {
    return coordinatorDistrict ? { ...filters, district: coordinatorDistrict } : filters;
  }, [filters, coordinatorDistrict]);

  const { data: reports, isLoading, refetch } = useQuery(
    ['reports', currentPage, effectiveFilters],
    () => api.reports.getAll({
      page: currentPage,
      limit: 10,
      ...effectiveFilters
    }),
    { keepPreviousData: true }
  );

  const { data: stats } = useQuery(
    ['reports-stats', effectiveFilters.district, effectiveFilters.constituency, effectiveFilters.timeline, effectiveFilters.status],
    () => api.reports.getStats({
      district: effectiveFilters.district || undefined,
      constituency: effectiveFilters.constituency || undefined,
      start_date: undefined,
      end_date: undefined,
    })
  );

  // Enhanced analytics from available report data
  const analytics = useMemo(() => {
    if (!reports?.data?.data) return null;

    const reportsData = reports.data.data;

    // District analysis
    const districtBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const district = report.district || 'Unknown';
      acc[district] = (acc[district] || 0) + 1;
      return acc;
    }, {});

    // Constituency analysis
    const constituencyBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const constituency = report.constituency || 'Unknown';
      acc[constituency] = (acc[constituency] || 0) + 1;
      return acc;
    }, {});

    // Timeline analysis
    const timelineBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const timeline = report.timeline || 'Unknown';
      acc[timeline] = (acc[timeline] || 0) + 1;
      return acc;
    }, {});

    // Status analysis
    const statusBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const status = report.status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Observer analysis
    const observerBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const observer = report.observer_name || 'Unknown';
      acc[observer] = (acc[observer] || 0) + 1;
      return acc;
    }, {});

    // Daily submission analysis
    const submissionBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const date = new Date(report.created_at).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    // Ward analysis
    const wardBreakdown = reportsData.reduce((acc: Record<string, number>, report: any) => {
      const ward = report.ward || 'Unknown';
      acc[ward] = (acc[ward] || 0) + 1;
      return acc;
    }, {});

    return {
      districtBreakdown,
      constituencyBreakdown,
      timelineBreakdown,
      statusBreakdown,
      observerBreakdown,
      submissionBreakdown,
      wardBreakdown,
      totalReports: reportsData.length,
      uniqueDistricts: Object.keys(districtBreakdown).length,
      uniqueConstituencies: Object.keys(constituencyBreakdown).length,
      uniqueObservers: Object.keys(observerBreakdown).length,
      uniqueWards: Object.keys(wardBreakdown).length
    };
  }, [reports]);


  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await api.reports.updateStatus(id, status);
      refetch();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.reports.delete(id);
      refetch();
    } catch (error) {
      console.error('Failed to delete report:', error);
    }
  };

  const handleViewReport = (reportId: string) => {
    setSelectedReportId(reportId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReportId(null);
  };


  const getStatusBadge = (status: string) => {
    const badges = {
      submitted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      reviewed: 'bg-blue-100 text-blue-800 border-blue-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTimelineBadge = (timeline: string) => {
    const badges = {
      morning: 'bg-blue-100 text-blue-800 border-blue-200',
      afternoon: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      evening: 'bg-orange-100 text-orange-800 border-orange-200',
      vote_count: 'bg-green-100 text-green-800 border-green-200',
      // Legacy support
      before_voting: 'bg-blue-100 text-blue-800 border-blue-200',
      during_voting: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      after_voting: 'bg-green-100 text-green-800 border-green-200',
    };
    return badges[timeline as keyof typeof badges] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (isLoading && !reports) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-lg p-8 text-center bg-white shadow-xl rounded-2xl">
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl">
              <DocumentTextIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="mb-4 text-2xl font-bold text-gray-800">Loading Reports</h2>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Professional Header */}
      <div className="sticky top-0 z-50 border-b bg-white/80 backdrop-blur-lg border-gray-200/50">
        <div className="px-6 py-8 mx-auto max-w-7xl">
          <div className="flex flex-col justify-between mb-8 lg:flex-row lg:items-center">
            <div className="mb-6 lg:mb-0">
              <div className="flex items-center mb-2 space-x-3">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="flex items-center px-4 py-2 space-x-2 text-gray-600 transition-colors bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-900"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                  <span>Back</span>
                </button>
                <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
                  <DocumentTextIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">
                    {user?.role === 'coordinator' && coordinatorDistrict
                      ? `Reports - ${coordinatorDistrict}`
                      : 'Observer Reports'}
                  </h1>
                  <p className="text-lg font-medium text-gray-600">
                    Comprehensive election monitoring & report management
                  </p>
                </div>
              </div>
            </div>

          
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8 mx-auto max-w-7xl">
        <AnimatePresence mode="wait">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-12"
          >
            {/* Professional Stats Cards */}
            {stats && (() => {
              const statsData = stats.data?.data || {};

              // Helper function to safely parse numbers
              const safeNumber = (value: any) => {
                if (typeof value === 'number') return value;
                if (typeof value === 'string') return parseInt(value) || 0;
                return 0;
              };

              const statsItems = [
                { title: "Total Reports", value: safeNumber(statsData.total_reports), icon: DocumentTextIcon, color: "from-blue-500 to-blue-600", bgColor: "from-blue-50 to-blue-100" },
                { title: "Morning Reports", value: safeNumber(statsData.morning), icon: ClockIcon, color: "from-yellow-500 to-orange-500", bgColor: "from-yellow-50 to-orange-100" },
                { title: "Afternoon Reports", value: safeNumber(statsData.afternoon), icon: ClockIcon, color: "from-purple-500 to-purple-600", bgColor: "from-purple-50 to-purple-100" },
                { title: "Vote Count", value: safeNumber(statsData.vote_count), icon: ClockIcon, color: "from-green-500 to-emerald-600", bgColor: "from-green-50 to-emerald-100" },
                { title: "Submitted", value: safeNumber(statsData.submitted), icon: DocumentTextIcon, color: "from-yellow-500 to-yellow-600", bgColor: "from-yellow-50 to-yellow-100" },
                { title: "Reviewed", value: safeNumber(statsData.reviewed), icon: DocumentTextIcon, color: "from-blue-500 to-indigo-600", bgColor: "from-blue-50 to-indigo-100" },
                { title: "Approved", value: safeNumber(statsData.approved), icon: DocumentTextIcon, color: "from-green-500 to-green-600", bgColor: "from-green-50 to-green-100" },
                ...(user?.role !== 'coordinator' ? [{ title: "Districts Covered", value: safeNumber(statsData.districts_covered), icon: MapPinIcon, color: "from-purple-500 to-pink-600", bgColor: "from-purple-50 to-pink-100" }] : []),
                { title: "Constituencies", value: safeNumber(statsData.constituencies_covered), icon: MapPinIcon, color: "from-indigo-500 to-blue-600", bgColor: "from-indigo-50 to-blue-100" },
              ];

              return (
                <div className="relative">
                  <div className="flex items-center mb-8 space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl">
                      <ChartBarIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Report Statistics</h2>
                      <p className="font-medium text-gray-600">
                        Overview of all election monitoring reports
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-indigo-500 to-purple-500"></div>

                  <div className="grid grid-cols-1 gap-6 mt-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                    {statsItems.map((item, index) => (
                      <motion.div
                        key={item.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-indigo-200"
                      >
                        <div className={`absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br ${item.bgColor} group-hover:opacity-100`}></div>
                        <div className="relative p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl bg-gradient-to-r ${item.color} shadow-lg`}>
                              <item.icon className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                              <span className="text-xs font-medium text-green-600">Live</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-indigo-700">
                              {formatNumber(item.value)}
                            </p>
                            <p className="text-sm font-medium text-gray-600 transition-colors duration-300 group-hover:text-gray-700">
                              {item.title}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              {formatNumberWithCommas(item.value)} total
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Professional Analytics Section */}
            {analytics && (
              <div className="space-y-8">
                <div className="relative">
                  <div className="flex items-center mb-8 space-x-4">
                    <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl">
                      <ChartBarIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Reports Analytics</h2>
                      <p className="font-medium text-gray-600">
                        Detailed insights and performance metrics
                      </p>
                    </div>
                  </div>
                  <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-purple-500 to-pink-500"></div>
                </div>

                {/* Professional Analytics Cards Grid */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  {/* Districts Analysis */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-blue-200"
                  >
                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 group-hover:opacity-100"></div>

                    {/* Header */}
                    <div className="relative p-6 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                          <MapPinIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Districts Coverage</h3>
                          <p className="text-sm text-white/80">Geographic distribution</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative p-6">
                      {/* District Data */}
                      <div className="h-64 overflow-y-auto border rounded-lg bg-gray-50">
                        <div className="p-3 space-y-2">
                          {Object.entries(analytics.districtBreakdown)
                            .sort(([, a], [, b]) => Number(b) - Number(a))
                            .map(([district, count], index) => {
                              const percentage = analytics.totalReports > 0 ? (Number(count) / analytics.totalReports * 100) : 0;
                              const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-cyan-500', 'bg-teal-500', 'bg-green-500', 'bg-emerald-500'];
                              const colorClass = index < 8 ? colors[index] : 'bg-gray-400';

                              return (
                                <div key={district} className="p-3 transition-shadow duration-200 bg-white border shadow-sm rounded-xl hover:shadow-md">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                      <div className={`w-3 h-3 rounded-full mr-3 ${colorClass} shadow-sm`}></div>
                                      <span className="font-semibold text-gray-800 truncate">{district}</span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-bold text-gray-900">{formatNumber(Number(count))}</div>
                                      <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                                    </div>
                                  </div>
                                  <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                                    <div
                                      className={`h-2 rounded-full ${colorClass} transition-all duration-500`}
                                      style={{ width: `${Math.max(percentage, 2)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Timeline Analysis */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-purple-200"
                  >
                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 group-hover:opacity-100"></div>

                    {/* Header */}
                    <div className="relative p-6 bg-gradient-to-br from-purple-500 via-pink-600 to-rose-600">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                          <ClockIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Timeline Distribution</h3>
                          <p className="text-sm text-white/80">Reporting periods</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative p-6">
                      <div className="space-y-4">
                        {Object.entries(analytics.timelineBreakdown).map(([timeline, count], index) => {
                          const percentage = analytics.totalReports > 0 ? (Number(count) / analytics.totalReports * 100) : 0;
                          const colors = ['bg-blue-500', 'bg-yellow-500', 'bg-red-500', 'bg-green-500'];
                          const colorClass = colors[index] || 'bg-gray-500';
                          const timelineLabels: Record<string, string> = {
                            morning: 'Morning (6-10am)',
                            afternoon: 'Afternoon (10am-2pm)',
                            evening: 'Evening (2-4pm)',
                            vote_count: 'Vote Count (4pm-12am)',
                            before_voting: 'Before Voting',
                            during_voting: 'During Voting',
                            after_voting: 'After Voting'
                          };

                          return (
                            <div key={timeline} className="p-4 transition-colors duration-200 bg-gray-50 rounded-xl hover:bg-gray-100">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                  <div className={`w-4 h-4 rounded-full mr-3 ${colorClass} shadow-sm`}></div>
                                  <span className="font-semibold text-gray-800">{timelineLabels[timeline] || timeline}</span>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-gray-900">{formatNumber(Number(count))}</div>
                                  <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                                </div>
                              </div>
                              <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                                <div
                                  className={`h-2 rounded-full ${colorClass} transition-all duration-500`}
                                  style={{ width: `${Math.max(percentage, 5)}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>

                  {/* Status Overview */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-green-200"
                  >
                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 group-hover:opacity-100"></div>

                    {/* Header */}
                    <div className="relative p-6 bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                          <DocumentTextIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Status Overview</h3>
                          <p className="text-sm text-white/80">Report processing</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative p-6">
                      <div className="space-y-4">
                        {Object.entries(analytics.statusBreakdown).map(([status, count]) => {
                          const percentage = analytics.totalReports > 0 ? (Number(count) / analytics.totalReports * 100) : 0;
                          const statusColors: Record<string, string> = {
                            submitted: 'bg-yellow-500',
                            reviewed: 'bg-blue-500',
                            approved: 'bg-green-500',
                            draft: 'bg-gray-500'
                          };
                          const colorClass = statusColors[status] || 'bg-gray-500';

                          return (
                            <div key={status} className="p-4 transition-colors duration-200 bg-gray-50 rounded-xl hover:bg-gray-100">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                  <div className={`w-4 h-4 rounded-full mr-3 ${colorClass} shadow-sm`}></div>
                                  <span className="font-semibold text-gray-800 capitalize">{status}</span>
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-gray-900">{formatNumber(Number(count))}</div>
                                  <div className="text-xs text-gray-500">{percentage.toFixed(1)}%</div>
                                </div>
                              </div>
                              <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                                <div
                                  className={`h-2 rounded-full ${colorClass} transition-all duration-500`}
                                  style={{ width: `${Math.max(percentage, 5)}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Secondary Analytics Row - Professional Observer & Timeline Cards */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Observer Performance */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-indigo-200"
                  >
                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 group-hover:opacity-100"></div>

                    {/* Header */}
                    <div className="relative p-6 bg-gradient-to-br from-indigo-500 via-blue-600 to-cyan-600">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                          <UserGroupIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Top Observers</h3>
                          <p className="text-sm text-white/80">Performance ranking</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative p-6">
                      <div className="h-64 overflow-y-auto border rounded-lg bg-gray-50">
                        <div className="p-3 space-y-2">
                          {Object.entries(analytics.observerBreakdown)
                            .sort(([, a], [, b]) => Number(b) - Number(a))
                            .slice(0, 10)
                            .map(([observer, count], index) => {
                              const percentage = analytics.totalReports > 0 ? (Number(count) / analytics.totalReports * 100) : 0;

                              return (
                                <div key={observer} className="p-3 transition-shadow duration-200 bg-white border shadow-sm rounded-xl hover:shadow-md">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                      <div className="flex items-center justify-center w-8 h-8 mr-3 text-xs font-bold text-white rounded-full bg-gradient-to-r from-indigo-500 to-blue-600">
                                        #{index + 1}
                                      </div>
                                      <span className="font-semibold text-gray-800 truncate">{observer}</span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-bold text-gray-900">{formatNumber(Number(count))}</div>
                                      <div className="text-xs text-gray-500">reports</div>
                                    </div>
                                  </div>
                                  <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                                    <div
                                      className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600"
                                      style={{ width: `${Math.max(percentage, 5)}%` }}
                                    ></div>
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">{percentage.toFixed(1)}% of total</div>
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Daily Submission Trends */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1 hover:border-orange-200"
                  >
                    <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 group-hover:opacity-100"></div>

                    {/* Header */}
                    <div className="relative p-6 bg-gradient-to-br from-orange-500 via-red-600 to-pink-600">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                          <CalendarIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Daily Submissions</h3>
                          <p className="text-sm text-white/80">Report trends</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative p-6">
                      <div className="h-64 overflow-y-auto border rounded-lg bg-gray-50">
                        <div className="p-3 space-y-2">
                          {Object.entries(analytics.submissionBreakdown)
                            .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
                            .slice(0, 7) // Show last 7 days
                            .map(([date, count]) => {
                              const maxCount = Math.max(...Object.values(analytics.submissionBreakdown).map(Number));
                              const percentage = maxCount > 0 ? (Number(count) / maxCount * 100) : 0;

                              return (
                                <div key={date} className="p-3 transition-shadow duration-200 bg-white border shadow-sm rounded-xl hover:shadow-md">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center">
                                      <div className="p-2 mr-3 rounded-lg bg-gradient-to-r from-orange-500 to-red-600">
                                        <CalendarIcon className="w-4 h-4 text-white" />
                                      </div>
                                      <span className="font-semibold text-gray-800">{date}</span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-lg font-bold text-gray-900">{formatNumber(Number(count))}</div>
                                      <div className="text-xs text-gray-500">reports</div>
                                    </div>
                                  </div>
                                  <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                                    <div
                                      className="h-2 transition-all duration-500 rounded-full bg-gradient-to-r from-orange-500 to-red-600"
                                      style={{ width: `${Math.max(percentage, 5)}%` }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Professional Summary Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="relative overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:-translate-y-1"
                >
                  <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 group-hover:opacity-100"></div>

                  {/* Header */}
                  <div className="relative p-8 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                          <ChartBarIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">Coverage Summary</h3>
                          <p className="font-medium text-white/80">Comprehensive election monitoring statistics</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="relative p-8">
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                      <div className="p-4 text-center bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl">
                        <div className="mb-2 text-3xl font-bold text-blue-600">{analytics.uniqueDistricts}</div>
                        <div className="text-sm font-semibold text-gray-700">Districts Covered</div>
                      </div>
                      <div className="p-4 text-center bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl">
                        <div className="mb-2 text-3xl font-bold text-green-600">{analytics.uniqueConstituencies}</div>
                        <div className="text-sm font-semibold text-gray-700">Constituencies</div>
                      </div>
                      <div className="p-4 text-center bg-gradient-to-br from-purple-50 to-pink-100 rounded-2xl">
                        <div className="mb-2 text-3xl font-bold text-purple-600">{analytics.uniqueWards}</div>
                        <div className="text-sm font-semibold text-gray-700">Wards Covered</div>
                      </div>
                      <div className="p-4 text-center bg-gradient-to-br from-orange-50 to-red-100 rounded-2xl">
                        <div className="mb-2 text-3xl font-bold text-orange-600">{analytics.uniqueObservers}</div>
                        <div className="text-sm font-semibold text-gray-700">Active Observers</div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Professional Filters Section */}
            <div className="relative">
              <div className="flex items-center mb-8 space-x-4">
                <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-gray-500 to-gray-600 rounded-2xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Filters & Search</h2>
                  <p className="font-medium text-gray-600">
                    Refine your report view
                  </p>
                </div>
              </div>
              <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-gray-500 to-gray-600"></div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="relative mt-8 overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:border-indigo-200"
            >
              <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 group-hover:opacity-100"></div>
              <div className="relative p-8">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">District</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:bg-white"
                      placeholder="Filter by district"
                      value={effectiveFilters.district}
                      onChange={(e) => setFilters({ ...filters, district: e.target.value })}
                      disabled={!!coordinatorDistrict}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">Constituency</label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:bg-white"
                      placeholder="Filter by constituency"
                      value={filters.constituency}
                      onChange={(e) => setFilters({ ...filters, constituency: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">Timeline</label>
                    <select
                      className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:bg-white"
                      value={filters.timeline}
                      onChange={(e) => setFilters({ ...filters, timeline: e.target.value })}
                    >
                      <option value="">All Timelines</option>
                      <option value="morning">Morning (6:00 AM - 12:00 PM)</option>
                      <option value="afternoon">Afternoon (12:00 PM - 4:00 PM)</option>
                      <option value="vote_count">Vote Count (4:00 PM - 4:00 AM)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-semibold text-gray-700">Status</label>
                    <select
                      className="w-full px-4 py-3 text-gray-900 transition-all duration-300 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:bg-white"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Statuses</option>
                      <option value="submitted">Submitted</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Professional Reports Table */}
            <div className="relative">
              <div className="flex items-center mb-8 space-x-4">
                <div className="flex items-center justify-center w-12 h-12 shadow-lg bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl">
                  <DocumentTextIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Observer Reports</h2>
                  <p className="font-medium text-gray-600">
                    {reports?.data.pagination ? `${formatNumberWithCommas(reports.data.pagination.total)} total reports` : 'Comprehensive report listing'}
                  </p>
                </div>
              </div>
              <div className="absolute left-0 w-24 h-1 rounded-full -bottom-2 bg-gradient-to-r from-green-500 to-emerald-600"></div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="relative mt-8 overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:border-indigo-200"
            >
              <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-br from-green-50 via-blue-50 to-indigo-50 group-hover:opacity-100"></div>
              <div className="relative overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Report ID</th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Observer</th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Location</th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Timeline</th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Status</th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Submitted</th>
                      <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reports?.data.data.map((report: any, index: number) => (
                      <motion.tr
                        key={report.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="transition-all duration-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="p-2 mr-3 rounded-lg bg-gradient-to-r from-blue-100 to-indigo-100">
                              <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="text-sm font-bold text-gray-900">
                              {report.report_id}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex items-center justify-center w-10 h-10 mr-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-600">
                              <span className="text-sm font-bold text-white">
                                {report.observer_name?.charAt(0) || 'O'}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {report.observer_name}
                              </div>
                              {report.user_name && (
                                <div className="text-xs text-gray-500">
                                  {report.user_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="flex items-center mb-1">
                              <MapPinIcon className="w-4 h-4 mr-2 text-blue-600" />
                              <span className="font-semibold text-gray-900">{report.district}</span>
                            </div>
                            <div className="ml-6 text-gray-600">{report.constituency}</div>
                            <div className="ml-6 text-xs text-gray-500">
                              {report.ward} - {report.center}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getTimelineBadge(report.timeline)}`}>
                            <ClockIcon className="w-3 h-3 mr-1" />
                            {report.timeline?.charAt(0).toUpperCase() + report.timeline?.slice(1).replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(report.status)}`}>
                            {report.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {new Date(report.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewReport(report.id.toString())}
                              className="inline-flex items-center px-3 py-2 text-xs font-semibold text-blue-600 transition-colors duration-200 bg-blue-100 rounded-lg hover:bg-blue-200"
                              title="View Details"
                            >
                              <EyeIcon className="w-4 h-4 mr-1" />
                              View
                            </button>
                            {report.status === 'submitted' && (
                              <button
                                onClick={() => handleStatusUpdate(report.id, 'reviewed')}
                                className="inline-flex items-center px-3 py-2 text-xs font-semibold text-green-600 transition-colors duration-200 bg-green-100 rounded-lg hover:bg-green-200"
                                title="Mark as Reviewed"
                              >
                                <CheckIcon className="w-4 h-4 mr-1" />
                                Review
                              </button>
                            )}
                            {report.status === 'reviewed' && (
                              <button
                                onClick={() => handleStatusUpdate(report.id, 'approved')}
                                className="inline-flex items-center px-3 py-2 text-xs font-semibold transition-colors duration-200 rounded-lg text-emerald-600 bg-emerald-100 hover:bg-emerald-200"
                                title="Approve"
                              >
                                <CheckIcon className="w-4 h-4 mr-1" />
                                Approve
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Professional Pagination */}
            {reports?.data.pagination && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="relative mt-6 overflow-hidden transition-all duration-500 bg-white border shadow-lg group backdrop-blur-sm border-gray-200/70 rounded-2xl hover:shadow-2xl hover:border-indigo-200"
              >
                <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-gradient-to-r from-blue-50 to-indigo-50 group-hover:opacity-100"></div>
                <div className="relative flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-4">
                    <div className="p-2 rounded-lg bg-gradient-to-r from-blue-100 to-indigo-100">
                      <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-sm font-semibold text-gray-700">
                      Showing{' '}
                      <span className="text-blue-600">
                        {formatNumberWithCommas(((reports.data.pagination.page - 1) * reports.data.pagination.limit) + 1)}
                      </span>{' '}
                      to{' '}
                      <span className="text-blue-600">
                        {formatNumberWithCommas(Math.min(reports.data.pagination.page * reports.data.pagination.limit, reports.data.pagination.total))}
                      </span>{' '}
                      of{' '}
                      <span className="font-bold text-blue-600">
                        {formatNumberWithCommas(reports.data.pagination.total)}
                      </span>{' '}
                      reports
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 transition-all duration-300 bg-gray-100 border border-gray-200 rounded-xl hover:bg-gray-200 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-4 h-4 mr-2" />
                      Previous
                    </button>
                    <div className="px-4 py-2 text-sm font-bold text-blue-600 bg-blue-100 rounded-xl">
                      Page {currentPage} of {reports.data.pagination.totalPages}
                    </div>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage >= reports.data.pagination.totalPages}
                      className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white transition-all duration-300 shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl"
                    >
                      Next
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Report Detail Modal */}
      <ReportDetailModal
        reportId={selectedReportId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onStatusUpdate={handleStatusUpdate}
        onDelete={handleDelete}
        isCoordinator={user?.role === 'coordinator'}
      />
    </div>
  );
};

export default Reports;
